<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations, animateValue } from '$lib/utils/animations';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  
  let scrollY = 0;
  let translateY = 0;
  
  // Make the parallax effect reactive
  $: translateY = scrollY * 0.5;

  let participantsCount = 0;
  let monthsCount = 0;
  let exhibitionsCount = 0;

  onMount(() => {
    // Setup number animations
    const metricsObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateValue(0, 250, 2000, (val) => participantsCount = val);
          animateValue(0, 12, 1000, (val) => monthsCount = val);
          animateValue(0, 3, 1500, (val) => exhibitionsCount = val);
          metricsObserver.disconnect();
        }
      });
    });

    const metricsSection = document.querySelector('.metrics-section');
    if (metricsSection) {
      metricsObserver.observe(metricsSection);
    }

    // Setup scroll animations
    setupProjectAnimations();
  });
</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroSplit
    title="The Gestalt of Digital Identity"
    client="University of Applied Sciences Potsdam"
    industry="Data-Vis"
    product="Award-winning thesis exploring how digital identities emerge from the gestalt of our online traces"
    heroMedia={{
      type: 'video',
      src: "https://vz-6b226b53-3c8.b-cdn.net/073c0f43-0bcd-4df7-9c1a-3ad4f629a4e4/playlist.m3u8"
    }}
    metrics={[
      { value: 250, label: "Participants" },
      { value: 5, label: "Awards" },
      { value: 8, label: "Exhibitions" }
    ]}
    roles="Research, data analysis, digital identity, privacy, and graduated with distinction."
  />

  <!-- Content -->
  <div class="project-section">
    <!-- Large Text Block -->
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90">
        Investigating how our digital footprints coalesce into perceived identities, and the implications for privacy and self-representation online.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="bg-gray-50 p-8 rounded-lg relative animate-on-view" data-delay="2">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">Research Focus</h2>
          <p class="mb-8 text-base text-gray-700">
            This project examines how digital identities emerge from the collective interpretation of our online traces, drawing parallels with Gestalt psychology's principles of perception.
          </p>
          <p class="text-base text-gray-700">
            Through a combination of data analysis and user studies, we explore how fragments of online activity are assembled into coherent digital personas by both algorithms and human observers.
          </p>
        </div>
      </div>

      <div class="bg-gray-50 p-8 rounded-lg relative animate-on-view" data-delay="3">
        <div class="relative z-10">
          <h2 class="text-2xl font-light mb-4">Methodology</h2>
          <p class="mb-8 text-base text-gray-700">
            The research combines quantitative analysis of digital footprints with qualitative interviews, examining how different types of online activity contribute to identity formation.
          </p>
          <p class="text-base text-gray-700">
            We developed tools to visualize and analyze patterns in participants' digital traces, revealing how seemingly disparate data points combine to create lasting impressions.
          </p>
        </div>
      </div>
    </div>

    <!-- Key Findings -->
    <div class="mt-24 container mx-auto">
      <h2 class="text-3xl font-light mb-12">Translating Digital Identities into Analog Sculptures</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
        <div class="space-y-8">
          <img 
            src="/projects/gestalt-digital-identity/Screenshot 2025-04-15 at 17.15.04.png" 
            alt="Digital Identity Pattern Analysis" 
            class="w-full rounded-lg shadow-lg animate-on-view"
            data-delay="4"
          />
        </div>
        <div class="flex flex-col justify-center">
          <h3 class="text-xl font-light mb-4">Crafting the Digital Identity</h3>
          <p class="text-gray-700 mb-8">
            The concept of a world devoid of the Internet is inconceivable for many. We increasingly utilize online platforms to create and continuously reshape digital realities. Alongside our tangible realm, a digital dimension has arisen, pulsating with information represented in bits and bytes.
          </p>
          <p class="text-gray-700">
            This project highlighted the contrast by representing digital entities in three-dimensional forms. In our pursuit to understand how digital identities are constructed, we established criteria for a standardized, abstract analysis.
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
        <div class="flex flex-col justify-center order-2 md:order-1">
          <h3 class="text-xl font-light mb-4">Data Collection & Analysis</h3>
          <p class="text-gray-700 mb-8">
            Web-crawling software was employed to methodically scan and evaluate specific online pages. The interpretation of the gathered personal data determined the distinct shape of each digital identity, culminating in a computer-generated sculpture.
          </p>
          <p class="text-gray-700">
            Data reflecting consumer habits (from platforms like Amazon), communication patterns (Twitter), interests (Delicious), and musical preferences (Last.fm) of select individuals were archived. This data was instrumental in crafting the sculptures.
          </p>
        </div>
        <div class="order-1 md:order-2">
          <img 
            src="/projects/gestalt-digital-identity/Screenshot 2025-04-15 at 17.15.50.png" 
            alt="Identity Management Strategies" 
            class="w-full rounded-lg shadow-lg animate-on-view"
            data-delay="5"
          />
        </div>
      </div>
    </div>

    <!-- Quote Section -->
    <section class="py-8 animate-on-view">
      <div class="container px-4 md:px-8 py-16 max-w-4xl">
        <div class="grid grid-cols-12">
          <div class="col-span-12">
            <div class="relative">
              <span 
                class="absolute -top-8 text-[120px] leading-none text-[#F96604] font-serif animate-on-view"
                aria-hidden="true"
                style="opacity: 0.1"
              >
                ”
              </span>
              <blockquote class="py-12 text-xl md:text-4xl font-light animate-on-view" style="color: hsla(226, 65%, 35%, 1); line-height: 1.4;">
                "Identity is no longer a given but a task: you have to create your own community... but bonds are not given once and for all, that they need to be negotiated perpetually."
                <footer class="mt-4 text-base text-gray-600">— Zygmunt Bauman</footer>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Visualization Process -->
    <div class="mt-24 container mx-auto">
      <h2 class="text-3xl font-light mb-12">Visualization Process</h2>
      <div class="grid grid-cols-1 gap-8">
        <img 
          src="/projects/gestalt-digital-identity/Screenshot 2025-04-15 at 17.16.24.png" 
          alt="Digital Identity Data Visualization" 
          class="w-full rounded-lg shadow-lg animate-on-view"
          data-delay="7"
        />
        <p class="text-base text-gray-700 mt-8 mb-12">
          In our quest to determine the best visualization method for this data, we considered various prototypes—from abstract landscapes to intricate three-dimensional networks. However, we opted for a more abstracted representation rather than presenting the data in an overtly discernible manner. For each digital identity, seven visual representations were created, leading to the production of tangible plaster sculptures.
        </p>
      </div>
    </div>

    <!-- Timeline Visualization -->
    <div class="mt-24 container mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 mb-24">
        <div class="md:col-span-2">
          <img 
            src="/projects/gestalt-digital-identity/Screenshot 2025-04-15 at 17.16.10.png" 
            alt="Digital Identity Timeline Analysis"
            class="w-full h-auto object-cover rounded-lg shadow-lg animate-on-view"
            data-delay="8"
          />
          <p class="text-base text-gray-700 mt-8">
            To showcase the diversity and relation of keywords, users created a timeline. Keywords from saved websites are chronologically displayed, with identical tags linked. The top three keywords for each community month are highlighted.
          </p>
        </div>
      </div>
    </div>

    <!-- Landscape Visualization -->
    <div class="mt-24 container mx-auto">
      <div class="grid grid-cols-1 gap-8">
        <img 
          src="/projects/gestalt-digital-identity/landscape_layer_unknown.png" 
          alt="Digital Identity Landscape"
          class="w-full h-auto object-cover rounded-lg shadow-lg animate-on-view"
          data-delay="9"
        />
        <p class="text-base text-gray-700 mt-8">
          A landscape formed by your personal data – Various formal studies were algorithmically generated to find the adequate representation.
        </p>
      </div>
    </div>

    <!-- Final Iterations -->
    <div class="mt-24 container mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="md:col-span-2">
          <img 
            src="/projects/gestalt-digital-identity/3822366591_b7832aa008_o.jpg" 
            alt="Final Digital Identity Visualization"
            class="w-full h-auto object-cover rounded-lg shadow-lg animate-on-view"
            data-delay="10"
          />
        </div>
        <div class="md:col-span-2">
          <img 
            src="/projects/gestalt-digital-identity/Screenshot 2025-04-15 at 17.16.35.png" 
            alt="Digital Identity Final Implementation"
            class="w-full h-auto object-cover rounded-lg shadow-lg animate-on-view"
            data-delay="11"
          />
          <p class="text-base text-gray-700 mt-8">
            In the final versions, time and continuity of the digital identity were taken into account.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Project Navigation -->
  <ProjectNav 
    prevProject={{ title: "Known Unknowns", url: "/projects/known-unknowns" }}
    nextProject={{ title: "Self Driving Car", url: "/projects/self-driving-car" }}
  />

  <Footer />
</div>

<style>
  /* Add any page-specific styles here */
</style> 